import React from 'react';

interface FooterProps {
  /** Primary accent color for hover states - defaults to blue */
  accentColor?: 'blue' | 'green' | 'purple' | 'orange' | 'red' | 'yellow' | 'pink' | 'cyan';
  /** Show the "Built with" tech stack line */
  showTechStack?: boolean;
  /** Custom tech stack text */
  techStackText?: string;
  /** Additional disclaimer/info text */
  disclaimer?: string;
  /** Background style variant */
  variant?: 'dark' | 'darker' | 'transparent';
  /** Brand name to show in the footer branding */
  brandName?: string;
  /** Optional brand link (defaults to none) */
  brandLinkHref?: string;
  /** Optional brand logo source */
  brandLogoSrc?: string;
  /** Built-by label (defaults to Jacob Kanfer) */
  builtByName?: string;
  /** Built-by link (defaults to jacobkanfer.com) */
  builtByHref?: string;
}

const accentColors = {
  blue: 'hover:text-blue-400',
  green: 'hover:text-green-400',
  purple: 'hover:text-purple-400',
  orange: 'hover:text-orange-400',
  red: 'hover:text-red-400',
  yellow: 'hover:text-yellow-400',
  pink: 'hover:text-pink-400',
  cyan: 'hover:text-cyan-400',
};

const bgVariants = {
  dark: 'bg-gray-900 border-t border-gray-700',
  darker: 'bg-gray-950 border-t border-gray-800',
  transparent: 'bg-transparent border-t border-gray-700/50',
};

/**
 * Reusable Footer Component
 * 
 * Drop this into any project for a consistent footer with JK branding.
 * Customize colors and content via props to match your project's theme.
 * 
 * Usage:
 * ```tsx
 * <Footer 
 *   accentColor="green"
 *   disclaimer="Practice responsibly"
 *   showTechStack={true}
 *   techStackText="Built with React + Vite"
 * />
 * ```
 */
export const Footer: React.FC<FooterProps> = ({
  accentColor = 'blue',
  showTechStack = true,
  techStackText = 'Built with React + Vite',
  disclaimer,
  variant = 'dark',
  brandName = 'JK Casino',
  brandLinkHref,
  brandLogoSrc = '/jk-logo.svg',
  builtByName = 'Jacob Kanfer',
  builtByHref = 'https://jacobkanfer.com',
}) => {
  const hoverColor = accentColors[accentColor];
  const bgClass = bgVariants[variant];

  return (
    <footer className={`py-6 ${bgClass} select-none`}>
      <div className="container mx-auto px-4">
        {/* Disclaimer if provided */}
        {disclaimer && (
          <p className="text-center text-gray-400 text-sm mb-4">
            {disclaimer}
          </p>
        )}

        {/* Main footer content */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 text-gray-500 text-sm">
          {/* Tech stack */}
          {showTechStack && (
            <span className="text-xs text-gray-600">
              {techStackText}
            </span>
          )}

          {/* Separator - hidden on mobile */}
          {(showTechStack) && (
            <span className="hidden sm:inline text-gray-700">•</span>
          )}

          {/* Built by (JK with logo) */}
          <a
            href={builtByHref}
            target="_blank"
            rel="noopener noreferrer"
            className={`flex items-center gap-2 text-gray-500 ${hoverColor} transition-colors group text-xs`}
            title={`Built by ${builtByName}`}
            aria-label={`Built by ${builtByName}`}
          >
            <img
              src={brandLogoSrc}
              alt="JK logo"
              className="w-5 h-5 opacity-70 group-hover:opacity-100 transition-opacity invert"
            />
            <span className="font-semibold group-hover:text-white transition-colors">Built by {builtByName}</span>
          </a>

          {/* Separator */}
          <span className="hidden sm:inline text-gray-700">•</span>

          {/* Built for brand */}
          {brandLinkHref ? (
            <a
              href={brandLinkHref}
              target="_blank"
              rel="noopener noreferrer"
              className={`flex items-center gap-2 text-gray-500 ${hoverColor} transition-colors group`}
              title={brandName}
              aria-label={`${brandName} home`}
            >
              <span className="text-xs">
                Built for<span className="font-semibold"> {brandName}</span>
              </span>
            </a>
          ) : (
            <span className="flex items-center gap-2 text-gray-500 text-xs">
              Built for<span className="font-semibold"> {brandName}</span>
            </span>
          )}
        </div>
      </div>
    </footer>
  );
};

export default Footer;

